/**
 * PADDLogo — brand icon recreated as an SVG component.
 *
 * The icon is a 4×4 geometric grid with quarter-circle arcs,
 * using PADD's official color palette. When `showWordmark` is true,
 * the full PADD lockup (icon + wordmark) is rendered.
 */
import React from "react";

// Official PADD brand colors
const C = {
  navyDark: "#0B181A",
  navy:     "#122326",
  slate:    "#213A3E",
  blue:     "#8BAFC4",
  olive:    "#C4CA80",
  terra:    "#BF6847",
};

// Each cell: x, y = top-left corner (20px grid)
// shape: 'rect' | 'arc' | 'circ'
// arc cells: arcCx, arcCy = circle center (r=20), clipped to the 20×20 cell
const CELLS = [
  // ─── Row 0 ────────────────────────────────────
  { x:  0, y:  0, shape: "arc",  arcCx:  0, arcCy:  0, fill: C.navy },   // SE quarter
  { x: 20, y:  0, shape: "rect",                        fill: C.blue },
  { x: 40, y:  0, shape: "arc",  arcCx: 60, arcCy:  0, fill: C.olive },  // SW quarter
  { x: 60, y:  0, shape: "circ",                        fill: C.terra },  // inscribed circle
  // ─── Row 1 ────────────────────────────────────
  { x:  0, y: 20, shape: "rect",                        fill: C.navy },
  { x: 20, y: 20, shape: "arc",  arcCx: 40, arcCy: 40, fill: C.navy },   // NW quarter
  { x: 40, y: 20, shape: "rect",                        fill: C.blue },
  { x: 60, y: 20, shape: "rect",                        fill: C.olive },
  // ─── Row 2 ────────────────────────────────────
  { x:  0, y: 40, shape: "arc",  arcCx:  0, arcCy: 60, fill: C.navyDark }, // NE quarter
  { x: 20, y: 40, shape: "rect",                        fill: C.slate },
  { x: 40, y: 40, shape: "arc",  arcCx: 60, arcCy: 60, fill: C.blue },   // NW quarter
  { x: 60, y: 40, shape: "rect",                        fill: C.navy },
  // ─── Row 3 ────────────────────────────────────
  { x:  0, y: 60, shape: "rect",                        fill: C.navyDark },
  { x: 20, y: 60, shape: "rect",                        fill: C.slate },
  { x: 40, y: 60, shape: "rect",                        fill: C.navy },
  { x: 60, y: 60, shape: "rect",                        fill: C.navyDark },
];

const ARC_CELLS = CELLS.filter((c) => c.shape === "arc");

/** The geometric 4×4 grid icon only */
export function PADDIcon({ size = 80, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      role="img"
      aria-label="PADD logo icon"
      className={className}
      focusable="false"
    >
      <defs>
        {ARC_CELLS.map((cell, i) => (
          <clipPath key={i} id={`padd-cp-${i}`}>
            <rect x={cell.x} y={cell.y} width="20" height="20" />
          </clipPath>
        ))}
      </defs>

      {CELLS.map((cell, i) => {
        if (cell.shape === "rect") {
          return (
            <rect
              key={i}
              x={cell.x}
              y={cell.y}
              width="20"
              height="20"
              fill={cell.fill}
            />
          );
        }
        if (cell.shape === "circ") {
          return (
            <circle
              key={i}
              cx={cell.x + 10}
              cy={cell.y + 10}
              r="10"
              fill={cell.fill}
            />
          );
        }
        if (cell.shape === "arc") {
          const arcIdx = ARC_CELLS.findIndex(
            (c) => c.x === cell.x && c.y === cell.y
          );
          return (
            <circle
              key={i}
              cx={cell.arcCx}
              cy={cell.arcCy}
              r="20"
              fill={cell.fill}
              clipPath={`url(#padd-cp-${arcIdx})`}
            />
          );
        }
        return null;
      })}
    </svg>
  );
}

/** Full primary lockup: icon + PADD wordmark + tagline */
export default function PADDLogo({
  iconSize = 44,
  variant = "full",   // "full" | "icon" | "compact"
  className,
}) {
  if (variant === "icon") return <PADDIcon size={iconSize} className={className} />;

  return (
    <div
      className={`padd-logo-lockup padd-logo-lockup--${variant} ${className || ""}`}
      role="img"
      aria-label="Panhandle Area Development District"
    >
      <PADDIcon size={iconSize} aria-hidden="true" />
      <div className="padd-logo-text">
        <span className="padd-logo-wordmark">PADD</span>
        {variant === "full" && (
          <span className="padd-logo-tagline">
            Panhandle Area Development District
          </span>
        )}
      </div>
    </div>
  );
}
